import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import JsonLd from "@/components/feature/JsonLd";
import { getArticleSchema } from "@/seo/schema";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articles, type PillarArticle as PillarArticleType } from "@/mocks/articles";
import { mainSiteUrl } from "@/mocks/categories";

interface PillarArticleProps {
  slug: string;
}

function resolveLang(lang: string | undefined): string {
  if (lang && ["en", "pl", "es", "fr"].includes(lang)) return lang;
  return "en";
}

export default function PillarArticle({ slug }: PillarArticleProps) {
  const { t, i18n } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const article: PillarArticleType | undefined = articles.find((a) => a.slug === slug);
  const lang = resolveLang(i18n.language);
  const content = article?.content[lang] || article?.content.en;

  if (!article || !content) {
    return (
      <>
        <Navbar />
        <main className="flex min-h-screen flex-col items-center justify-center px-4 pt-16">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">404</h1>
          <p className="mt-3 text-sm text-foreground-600">Article not found.</p>
          <Link
            to="/"
            className="mt-6 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50"
          >
            Back to Home
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const related = articles.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <>
      <JsonLd
        data={getArticleSchema({
          headline: content.title,
          description: content.subtitle,
          image: article.image,
        })}
      />
      <Navbar />
      <main className="pt-20">
        <article className="mx-auto max-w-3xl px-4 py-8 md:px-6">
          {/* 面包屑（含回主页内链） */}
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-foreground-900">
              {t("article.breadcrumbHome")}
            </Link>
            <span className="text-foreground-300">/</span>
            <Link to="/#guides" className="transition-colors hover:text-foreground-900">
              {t("guides.title")}
            </Link>
            <span className="text-foreground-300">/</span>
            <span className="text-foreground-700">{content.title}</span>
          </nav>

          <header className="mt-6">
            <span className="inline-block rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-900">
              {content.category}
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {content.title}
            </h1>
            <p className="mt-4 text-base leading-relaxed text-foreground-600 md:text-lg">
              {content.subtitle}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line"></i>
                </span>
                {t("article.author")}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line"></i>
                </span>
                {t("article.published")}: 2026
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line"></i>
                </span>
                {content.readingTime}
              </span>
            </div>
          </header>

          <div className="mt-8 overflow-hidden rounded-lg border border-background-200">
            <img
              src={article.image}
              alt={content.title}
              title={`${content.title} — USFans Spreadsheet`}
              className="h-56 w-full object-cover object-top md:h-72"
            />
          </div>

          <p className="mt-8 text-base leading-relaxed text-foreground-700 md:text-lg">
            {content.intro}
          </p>

          {content.sections.map((s) => (
            <section key={s.h2} className="mt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                {s.h2}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">{s.p}</p>
            </section>
          ))}

          {/* FAQ */}
          <section className="mt-10">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {t("article.faq.title")}
            </h2>
            <div className="mt-4 space-y-3">
              {content.faqs.map((f, i) => {
                const isOpen = openFaq === i;
                return (
                  <div
                    key={f.q}
                    className="overflow-hidden rounded-lg border border-background-200 bg-background-50"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : i)}
                      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                    >
                      <span className="text-sm font-semibold text-foreground-900">{f.q}</span>
                      <span className="flex w-6 h-6 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-600">
                        <i className={isOpen ? "ri-subtract-line" : "ri-add-line"}></i>
                      </span>
                    </button>
                    {isOpen && (
                      <div className="border-t border-background-200 px-5 py-4">
                        <p className="text-sm leading-relaxed text-foreground-600">{f.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* 相关文章 + 内链 */}
          <section className="mt-10 rounded-lg border border-background-200 bg-background-50 p-6">
            <h2 className="font-heading text-base font-bold text-foreground-950">
              {t("article.related.title")}
            </h2>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {related.map((r) => {
                const rc = r.content[lang] || r.content.en;
                return (
                  <Link
                    key={r.slug}
                    to={`/${r.slug}`}
                    className="group flex flex-col gap-2 rounded-md border border-background-200 p-3 transition-colors hover:border-primary-300"
                  >
                    <span className="flex w-8 h-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                      <i className="ri-article-line"></i>
                    </span>
                    <span className="text-sm font-semibold leading-snug text-foreground-800 group-hover:text-primary-800">
                      {rc.title}
                    </span>
                  </Link>
                );
              })}
            </div>
            <div className="mt-5 flex flex-col gap-2 border-t border-background-200 pt-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-700 transition-colors hover:text-primary-900"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </span>
                {t("article.related.back")}
              </Link>
              <Link
                to="/#categories"
                className="inline-flex items-center gap-2 text-sm font-medium text-foreground-700 transition-colors hover:text-foreground-950"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-grid-line"></i>
                </span>
                {t("article.related.categories")}
              </Link>
            </div>
          </section>
        </article>

        {/* CTA */}
        <section className="mx-auto max-w-3xl px-4 pb-16 md:px-6">
          <div className="rounded-2xl bg-primary-500 px-6 py-10 text-center md:px-12">
            <h2 className="font-heading text-2xl font-bold text-background-50">
              {t("article.cta.title")}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-background-50/90">
              {t("article.cta.text")}
            </p>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-background-100"
            >
              {t("article.cta.button")}
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line"></i>
              </span>
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}